import { useEffect, useState } from 'react';
import {
  GROUP_KINDS,
  GROUP_KIND_HINT,
  GROUP_KIND_LABEL,
  type AdminUserRow,
  type GroupKind,
  type GroupMemberRow,
  type GroupRow,
} from 'shared';
import { api } from '../api';
import NumberField from '../NumberField';
import AdminList from '../AdminList';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';
import { alert, confirm, haptic } from '../telegram';
import { RegionMultiPicker as RegionPicker } from '../RegionPicker';

const EMPTY = {
  name: '',
  kind: 'RESTRICTION' as GroupKind,
  description: '',
  blockedFromRegions: [] as string[],
  blockedToRegions: [] as string[],
  allowedFromRegions: [] as string[],
  allowedToRegions: [] as string[],
  priority: 0,
};

const KINDS = ['', ...GROUP_KINDS] as const;

/**
 * Группы: список и разбор.
 *
 * У группы две несвязанные стороны — правила видимости и состав, — и в
 * разборе они разделены переключателем, а не свалены в одну прокрутку.
 * Состав бывает в сорок человек, и листать его каждый раз, когда нужно
 * поправить регион, незачем.
 */
export default function AdminGroups() {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  /** Что разбираем: `new` — создаём, идентификатор — правим, `null` — закрыто. */
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [tab, setTab] = useState<'rules' | 'members'>('rules');

  const [members, setMembers] = useState<GroupMemberRow[]>([]);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<AdminUserRow[]>([]);

  const [kind, setKind] = useState<'' | GroupKind>('');
  const [query, setQuery] = useState('');

  async function load() {
    setLoading(true);
    try {
      setGroups((await api.admin.groups()).items);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFound([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setFound((await api.admin.users({ q: search, limit: 15 })).items);
      } catch {
        setFound([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  async function startEdit(g?: GroupRow) {
    setTab('rules');
    setSearch('');
    setFound([]);
    setMembers([]);

    if (!g) {
      setEditing('new');
      setForm({ ...EMPTY });
      return;
    }

    setEditing(g.id);
    setForm({
      name: g.name,
      kind: g.kind,
      description: g.description ?? '',
      blockedFromRegions: g.blockedFromRegions,
      blockedToRegions: g.blockedToRegions,
      allowedFromRegions: g.allowedFromRegions,
      allowedToRegions: g.allowedToRegions,
      priority: g.priority,
    });

    try {
      setMembers((await api.admin.members(g.id)).items);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function save() {
    if (form.name.trim().length < 2) return alert('Укажите название группы');
    setBusy(true);
    try {
      const payload = { ...form, description: form.description || undefined };
      if (editing === 'new') await api.admin.createGroup(payload);
      else if (editing) await api.admin.updateGroup(editing, payload);
      setEditing(null);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(g: GroupRow) {
    if (!(await confirm(`Удалить группу «${g.name}»? Участники потеряют её правила.`))) return;
    setBusy(true);
    try {
      await api.admin.deleteGroup(g.id);
      setEditing(null);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(g: GroupRow) {
    setBusy(true);
    try {
      await api.admin.updateGroup(g.id, { active: !g.active });
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addMember(u: AdminUserRow) {
    if (!editing || editing === 'new') return;
    setBusy(true);
    try {
      await api.admin.addMembers(editing, [u.id]);
      setMembers((await api.admin.members(editing)).items);
      setSearch('');
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(userId: string) {
    if (!editing || editing === 'new') return;
    setBusy(true);
    try {
      await api.admin.removeMember(editing, userId);
      setMembers((m) => m.filter((x) => x.userId !== userId));
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const open = groups.find((g) => g.id === editing) ?? null;

  const q = query.trim().toLowerCase();
  const shown = groups
    .filter((g) => (kind ? g.kind === kind : true))
    .filter((g) =>
      q ? [g.name, g.description ?? ''].join(' ').toLowerCase().includes(q) : true,
    );

  if (loading) return <div className="empty">Загрузка…</div>;

  return (
    <>
      <button className="anew" onClick={() => void startEdit()}>
        Создать группу
      </button>

      <AdminFilters
        active={kind === '' ? 0 : 1}
        search={
          <input
            placeholder="Название или описание"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        {KINDS.map((k) => (
          <button
            key={k || 'all'}
            type="button"
            className={kind === k ? 'chip chip--on' : 'chip'}
            onClick={() => {
              haptic();
              setKind(k);
            }}
          >
            {k ? GROUP_KIND_LABEL[k] : 'Все'}
          </button>
        ))}
      </AdminFilters>

      <AdminList
        rows={shown}
        keyOf={(g) => g.id}
        empty="Групп пока нет"
        // Выключенная группа подсвечена: её правила не действуют, и
        // объяснение «почему участник видит не то» чаще всего здесь.
        highlight={(g) => !g.active}
        action={{ label: 'Открыть', onClick: (g) => void startEdit(g) }}
        columns={[
          {
            title: 'Группа',
            cell: (g) => (
              <>
                <strong>{g.name}</strong>
                <span className="meta" style={{ display: 'block' }}>
                  {g.description || GROUP_KIND_HINT[g.kind]}
                </span>
              </>
            ),
          },
          {
            title: 'Тип',
            width: '150px',
            cell: (g) => (
              <span
                className={`badge ${g.kind === 'PRIVILEGE' ? 'badge--direct' : 'badge--warn'}`}
              >
                {GROUP_KIND_LABEL[g.kind]}
              </span>
            ),
          },
          {
            title: 'Участников',
            width: '120px',
            labelOnPhone: true,
            inline: true,
            cell: (g) => <span className="tabular">{g.membersCount}</span>,
          },
          {
            title: 'Закрытых грузов',
            width: '140px',
            labelOnPhone: true,
            inline: true,
            cell: (g) => <span className="tabular">{g.privateCargosCount}</span>,
          },
          {
            title: 'Приоритет',
            width: '110px',
            labelOnPhone: true,
            inline: true,
            cell: (g) => <span className="tabular">{g.priority}</span>,
          },
          {
            title: 'Состояние',
            width: '120px',
            labelOnPhone: true,
            inline: true,
            cell: (g) => (g.active ? 'включена' : 'выключена'),
          },
        ]}
      />

      <AdminSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Новая группа' : (open?.name ?? '')}
        subtitle={
          open && (
            <>
              {GROUP_KIND_LABEL[open.kind]} · участников {open.membersCount} · закрытых
              грузов {open.privateCargosCount}
              {!open.active && ' · выключена'}
            </>
          )
        }
        actions={
          <>
            {/* «Сохранить» только там, где есть что сохранять: в составе
                группы правится не форма, а список — каждое добавление и
                удаление уходит на сервер сразу. */}
            {(!open || tab === 'rules') && (
              <button disabled={busy} onClick={() => void save()}>
                Сохранить
              </button>
            )}
            <button className="secondary" onClick={() => setEditing(null)}>
              {!open || tab === 'rules' ? 'Отмена' : 'Закрыть'}
            </button>
            {open && (
              <>
                <button className="secondary" disabled={busy} onClick={() => void toggle(open)}>
                  {open.active ? 'Выключить' : 'Включить'}
                </button>
                <button className="danger" disabled={busy} onClick={() => void remove(open)}>
                  Удалить
                </button>
              </>
            )}
          </>
        }
      >
        {/* У новой группы состава ещё нет — переключать нечего. */}
        {open && (
          <div className="seg" style={{ marginBottom: 'var(--s3)' }}>
            <button
              type="button"
              className={tab === 'rules' ? 'seg__btn seg__btn--on' : 'seg__btn'}
              onClick={() => setTab('rules')}
            >
              Правила
            </button>
            <button
              type="button"
              className={tab === 'members' ? 'seg__btn seg__btn--on' : 'seg__btn'}
              onClick={() => setTab('members')}
            >
              Участники ({open.membersCount})
            </button>
          </div>
        )}

        {(!open || tab === 'rules') && (
          <>
            <div className="field">
              <label>Название</label>
              <input
                placeholder="Например: Только Северо-Запад"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="field">
              <label>Тип</label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as GroupKind })}
              >
                {GROUP_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {GROUP_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
              <div className="meta" style={{ marginTop: 4 }}>
                {GROUP_KIND_HINT[form.kind]}
              </div>
            </div>

            <div className="field">
              <label>Описание</label>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <RegionPicker
              label="Видны только эти регионы загрузки"
              hint="Пусто — ограничения нет. Строже блокировок и применяется первым."
              value={form.allowedFromRegions}
              onChange={(v) => setForm({ ...form, allowedFromRegions: v })}
            />
            <RegionPicker
              label="Видны только эти регионы выгрузки"
              value={form.allowedToRegions}
              onChange={(v) => setForm({ ...form, allowedToRegions: v })}
            />
            <RegionPicker
              label="Скрыть загрузку из регионов"
              value={form.blockedFromRegions}
              onChange={(v) => setForm({ ...form, blockedFromRegions: v })}
            />
            <RegionPicker
              label="Скрыть выгрузку в регионы"
              value={form.blockedToRegions}
              onChange={(v) => setForm({ ...form, blockedToRegions: v })}
            />

            <div className="field">
              <label>Приоритет при пересечении групп</label>
              <NumberField
                decimal={false}
                negative={false}
                value={form.priority}
                onChange={(v) => setForm({ ...form, priority: v })}
              />
            </div>
          </>
        )}

        {open && tab === 'members' && (
          <>
            <div className="field">
              <label>Добавить участника</label>
              <input
                placeholder="Поиск по логину, имени, телефону"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {found.map((u) => (
              <div key={u.id} className="row" style={{ marginBottom: 8 }}>
                <div className="meta" style={{ flex: 1 }}>
                  {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.login}
                  {u.login && ` · @${u.login}`}
                </div>
                <button
                  className="small"
                  disabled={busy || members.some((m) => m.userId === u.id)}
                  onClick={() => void addMember(u)}
                >
                  {members.some((m) => m.userId === u.id) ? 'уже в группе' : 'Добавить'}
                </button>
              </div>
            ))}

            {members.length === 0 && <div className="meta">Участников нет.</div>}

            {members.map((m) => (
              <div key={m.id} className="row" style={{ marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div>{m.name}</div>
                  <div className="meta">
                    {m.login ? `@${m.login}` : m.phone || '—'}
                    {m.banned && ' · в бане'}
                    {m.addedByName && ` · добавил ${m.addedByName}`}
                  </div>
                </div>
                <button
                  className="danger small"
                  disabled={busy}
                  onClick={() => void removeMember(m.userId)}
                >
                  убрать
                </button>
              </div>
            ))}
          </>
        )}
      </AdminSheet>
    </>
  );
}
